// Rooms + devices for a location — mirrors the Flutter Rooms screen, but shows
// everything on one page: the location's Common Devices, then every Room with
// the devices in it. Rooms come from GetRoomsbyUserIDandLocationId; each room's
// devices from GetDeviceDetailsbyUserIdAndRoomId; common devices from
// GetCommonDevicesbyUserIDandLocationId.

import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Typography, CircularProgress, AppBar, Toolbar, IconButton, Grid,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { getRooms, getCommonDevices, getDevicesByRoom } from '../api/api';
import { useApp } from '../state/AppContext';
import DeviceTile from '../components/DeviceTile';

const asArray = (res) => (res && Array.isArray(res.data) ? res.data : []);

export default function RoomsScreen() {
  const { locationId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { userId } = useApp();
  const [groups, setGroups] = useState([]); // ordered: [{ key, name, devices }]
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const rooms = asArray(await getRooms(userId, locationId));
        // Common devices + each room's devices, in parallel.
        const [common, ...roomDevs] = await Promise.all([
          getCommonDevices(userId, locationId).catch(() => null),
          ...rooms.map((r) => getDevicesByRoom(userId, r.RoomID).catch(() => null)),
        ]);

        const next = [];
        const commonDevices = asArray(common);
        if (commonDevices.length) {
          next.push({ key: 'common', name: 'Common Devices', devices: commonDevices });
        }
        rooms.forEach((r, i) => {
          next.push({
            key: `room-${r.RoomID}`,
            roomId: r.RoomID,
            name: r.RoomName || `Room ${r.RoomID}`,
            devices: asArray(roomDevs[i]),
          });
        });
        if (alive) setGroups(next);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [userId, locationId]);

  const openDevice = (d) =>
    navigate(`/device/${d.UniqueNumber}`, { state: { device: d } });

  const isEmpty = groups.length === 0;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static" elevation={2}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flex: 1, textAlign: 'center' }}>
            {state?.name || 'Rooms'}
          </Typography>
          <Box sx={{ width: 40 }} />
        </Toolbar>
      </AppBar>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
      ) : isEmpty ? (
        <Typography sx={{ textAlign: 'center', mt: 6 }} color="text.secondary">
          No rooms or devices in this location
        </Typography>
      ) : (
        <Box sx={{ p: 2 }}>
          {groups.map((g) => (
            <Box key={g.key} sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <MeetingRoomIcon sx={{ color: 'text.primary' }} fontSize="small" />
                <Typography variant="h6" color="text.primary">{g.name}</Typography>
                {g.roomId != null && (
                  <IconButton
                    size="small"
                    sx={{ color: 'primary.main' }}
                    aria-label={`Scenes for ${g.name}`}
                    onClick={() => navigate(`/room/${g.roomId}/scenes`, {
                      state: { name: g.name, devices: g.devices },
                    })}
                  >
                    <AutoAwesomeIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              {g.devices.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ pl: 0.5 }}>
                  No devices in this room
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  {g.devices.map((d) => (
                    <Grid item xs={6} sm={4} md={3} key={d.UniqueNumber}>
                      <DeviceTile device={d} onClick={() => openDevice(d)} />
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
